import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ApiService from "../../services/api";
import { useWhiteLabel } from "../../contexts/WhiteLabelContext";
import { WhiteLabelButton } from "../../utils/whiteLabelUtils";
import { ArrowLeft } from "lucide-react";

interface Clinic {
  clinic_id: number;
  clinic_name: string;
}

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const {
    contactLink,
    primaryColor,
    isWhiteLabel,
    logoUrl,
    brandName,
    blogs_link,
    clinic_id,
  } = useWhiteLabel();
  const [step, setStep] = useState<"form" | "otp">("form");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    clinicId: "",
    password: "",
    confirmPassword: "",
  });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [isLoadingClinics, setIsLoadingClinics] = useState(true);

  useEffect(() => {
    fetchClinics();
  }, []);

  const fetchClinics = async () => {
    try {
      const data = await ApiService.getClinics({
        ...(clinic_id != null && +clinic_id && { clinic_id: clinic_id }),
      });
      if (data.success && data.data) {
        setClinics(data.data);
      }
    } catch (error) {
      console.error("Error fetching clinics:", error);
    } finally {
      setIsLoadingClinics(false);
    }
  };

  const validateField = (name: string, value: string) => {
    let error = "";

    switch (name) {
      case "firstName":
        if (!value.trim()) error = "First name is required";
        else if (value.trim().length < 2)
          error = "First name must be at least 2 characters";
        break;
      case "lastName":
        if (!value.trim()) error = "Last name is required";
        else if (value.trim().length < 2)
          error = "Last name must be at least 2 characters";
        break;
      case "email":
        if (!value) error = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          error = "Please enter a valid email";
        break;
      case "phone":
        if (!value) error = "Phone number is required";
        else if (!/^\d{10,}$/.test(value.replace(/\D/g, "")))
          error = "Please enter a valid phone number";
        break;
      case "dateOfBirth":
        if (!value) error = "Date of birth is required";
        else {
          const date = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - date.getFullYear();
          if (age < 18) error = "You must be at least 18 years old";
          if (date > today) error = "Date of birth cannot be in the future";
        }
        break;
      case "clinicId":
        if (!value) error = "Please select a clinic";
        break;
      case "password":
        if (!value) error = "Password is required";
        else if (value.length < 6)
          error = "Password must be at least 6 characters";
        break;
      case "confirmPassword":
        if (!value) error = "Please confirm your password";
        else if (value !== formData.password) error = "Passwords do not match";
        break;
    }

    return error;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate on change if field has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    Object.keys(formData).forEach((key) => {
      if (key !== "confirmPassword" || formData.password) {
        const error = validateField(
          key,
          formData[key as keyof typeof formData]
        );
        if (error) newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce(
      (acc, key) => ({
        ...acc,
        [key]: true,
      }),
      {}
    );
    setTouched(allTouched);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const data = await ApiService.signup({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone_number: formData.phone,
        dob: formData.dateOfBirth, // YYYY-MM-DD format
        clinic_id: parseInt(formData.clinicId),
      });

      if (!data.success) {
        throw new Error(data.message || "Signup failed");
      }

      // Move to OTP verification step
      setStep("otp");
    } catch (err: any) {
      setErrors({
        submit:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to create account. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setErrors({ otp: "Please enter a valid 6-digit OTP" });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const data = await ApiService.verifyOtpSignup(formData.email, otpValue);

      if (!data.success) {
        throw new Error(data.message || "OTP verification failed");
      }

      // Navigate to success page or login
      navigate("/login");
    } catch (err: any) {
      setErrors({
        otp:
          err?.response?.data?.message ||
          err?.message ||
          "Invalid OTP. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (step === "otp") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* OTP Verification */}
        <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-6 sm:space-y-8 bg-white p-6 sm:p-8 rounded-lg shadow-lg border border-gray-200">
            <div className="text-center">
              <div className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-lg flex items-center justify-center">
                <img
                  // src="/assets/logo.svg"
                  // alt="Gattaca Genomics Logo"
                  src={logoUrl}
                  alt={`${brandName} Logo"`}
                  // className="h-100 w-100"
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/assets/logo.svg";
                  }}
                />
              </div>
              <h2 className="mt-4 sm:mt-6 text-xl sm:text-2xl font-bold text-gray-900">
                Verify your email
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-gray-600">
                We've sent a verification code to
                <br />
                <span className="font-medium">{formData.email}</span>
              </p>
            </div>

            <div className="mt-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter verification code
              </label>
              <div className="flex justify-center space-x-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
                    maxLength={1}
                  />
                ))}
              </div>

              {errors.otp && (
                <div className="mt-2 text-red-600 text-xs sm:text-sm">
                  {errors.otp}
                </div>
              )}

              <WhiteLabelButton
                onClick={handleVerifyOtp}
                disabled={isLoading || otp.join("").length !== 6}
                className="mt-6 w-full bg-blue-600 text-white py-2.5 sm:py-3 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Verifying..." : "Verify Email"}
              </WhiteLabelButton>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="inline-flex items-center text-xs sm:text-sm font-medium hover:underline"
                  style={{ color: primaryColor }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to log in
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 sm:space-y-8 bg-white p-6 sm:p-8 rounded-lg shadow-lg border border-gray-200">
          {/* Logo */}
          <div className="text-center">
            <div className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-lg flex items-center justify-center">
              <img
                // src="/assets/logo.svg"
                // alt="Gattaca Genomics Logo"
                src={logoUrl}
                alt={`${brandName} Logo"`}
                // className="h-100 w-100"
                className="h-full w-full object-contain"
                onError={(e) => {
                  e.currentTarget.src = "/assets/logo.svg";
                }}
              />
            </div>
            <h2 className="mt-4 sm:mt-6 text-xl sm:text-2xl font-bold text-gray-900">
              Create your account
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-600">
              Join our platform to access genetic testing services
            </p>
          </div>

          {/* Form */}
          <form className="mt-6 sm:mt-8 space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  First name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`appearance-none relative block w-full px-3 py-2 border ${
                    errors.firstName && touched.firstName
                      ? "border-red-500"
                      : "border-gray-300"
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && touched.firstName && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Last name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`appearance-none relative block w-full px-3 py-2 border ${
                    errors.lastName && touched.lastName
                      ? "border-red-500"
                      : "border-gray-300"
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && touched.lastName && (
                  <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`appearance-none relative block w-full px-3 py-2 border ${
                  errors.email && touched.email
                    ? "border-red-500"
                    : "border-gray-300"
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm`}
                placeholder="Enter your email"
              />
              {errors.email && touched.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Date of birth *
              </label>
              <input
                type="date"
                name="dateOfBirth"
                required
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`appearance-none relative block w-full px-3 py-2 border ${
                  errors.dateOfBirth && touched.dateOfBirth
                    ? "border-red-500"
                    : "border-gray-300"
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm`}
                max={new Date().toISOString().split("T")[0]} // Prevent future dates
              />
              {errors.dateOfBirth && touched.dateOfBirth && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.dateOfBirth}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Phone number *
              </label>
              <div className="flex">
                <select className="px-2 sm:px-3 py-2 border border-gray-300 rounded-l-md text-gray-900 text-sm">
                  <option>+1 (US)</option>
                </select>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`appearance-none relative block w-full px-3 py-2 border ${
                    errors.phone && touched.phone
                      ? "border-red-500"
                      : "border-gray-300"
                  } placeholder-gray-500 text-gray-900 rounded-r-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm`}
                  placeholder="Enter your phone number"
                />
              </div>
              {errors.phone && touched.phone && (
                <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Clinic *
              </label>
              <select
                name="clinicId"
                required
                value={formData.clinicId}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoadingClinics}
                className={`appearance-none relative block w-full px-3 py-2 border ${
                  errors.clinicId && touched.clinicId
                    ? "border-red-500"
                    : "border-gray-300"
                } text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm`}
              >
                <option value="">Select your clinic</option>
                {clinics.map((clinic) => (
                  <option key={clinic.clinic_id} value={clinic.clinic_id}>
                    {clinic.clinic_name}
                  </option>
                ))}
              </select>
              {errors.clinicId && touched.clinicId && (
                <p className="mt-1 text-xs text-red-600">{errors.clinicId}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <input
                type="password"
                name="password"
                required
                value={formData.password}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`appearance-none relative block w-full px-3 py-2 border ${
                  errors.password && touched.password
                    ? "border-red-500"
                    : "border-gray-300"
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm`}
                placeholder="Create a password (min 6 characters)"
              />
              {errors.password && touched.password && (
                <p className="mt-1 text-xs text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Confirm Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                required
                value={formData.confirmPassword}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`appearance-none relative block w-full px-3 py-2 border ${
                  errors.confirmPassword && touched.confirmPassword
                    ? "border-red-500"
                    : "border-gray-300"
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm`}
                placeholder="Confirm your password"
              />
              {errors.confirmPassword && touched.confirmPassword && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {errors.submit}
              </div>
            )}

            <div className="pt-2">
              <WhiteLabelButton
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Creating account..." : "Get started"}
              </WhiteLabelButton>
            </div>

            <div className="text-center text-xs sm:text-sm">
              <span className="text-gray-600">Already have an account? </span>
              <Link
                to="/login"
                // className="font-medium text-blue-600 hover:text-blue-500"
                className="font-medium hover:underline"
                style={{ color: primaryColor }}
              >
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
